/**
 * Local document storage using IndexedDB.
 * Used as a temporary store for purchase order files (remitos + invoice PDFs)
 * while S3 bucket is not yet configured.
 *
 * Once S3 is active, fileUrl will be a real HTTPS URL and this store
 * will only be needed for backwards compatibility with existing local:// references.
 */

const DB_NAME = 'erp-local-docs';
const DB_VERSION = 1;
const STORE = 'documents';

export interface LocalDocument {
  id: string;
  orderId: string;
  type: 'remito' | 'invoice_file';
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string; // ISO date string
}

interface StoredDocument extends LocalDocument {
  blob: Blob;
}

let _db: IDBDatabase | null = null;

function openDb(): Promise<IDBDatabase> {
  if (_db) return Promise.resolve(_db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('orderId', 'orderId', { unique: false });
      }
    };
    req.onsuccess = () => {
      _db = req.result;
      resolve(_db);
    };
    req.onerror = () => reject(req.error);
  });
}

function genId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export const localDocumentStore = {
  async save(doc: Omit<StoredDocument, 'id'>): Promise<string> {
    const db = await openDb();
    const id = genId();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const req = tx.objectStore(STORE).add({ ...doc, id });
      req.onsuccess = () => resolve(id);
      req.onerror = () => reject(req.error);
    });
  },

  async getByOrder(orderId: string): Promise<LocalDocument[]> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).index('orderId').getAll(orderId);
      req.onsuccess = () => {
        // Strip blob before returning to avoid large allocations in state
        resolve(req.result.map(({ blob: _blob, ...meta }: StoredDocument) => meta));
      };
      req.onerror = () => reject(req.error);
    });
  },

  async getBlob(id: string): Promise<Blob | null> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(id);
      req.onsuccess = () => resolve((req.result as StoredDocument | undefined)?.blob ?? null);
      req.onerror = () => reject(req.error);
    });
  },

  async delete(id: string): Promise<void> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const req = tx.objectStore(STORE).delete(id);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  },

  /** Returns a temporary blob URL for viewing. Caller must revoke it when done. */
  async createObjectUrl(id: string): Promise<string | null> {
    const blob = await this.getBlob(id);
    if (!blob) return null;
    return URL.createObjectURL(blob);
  },

  /** Returns per-order remito counts for all orders in the store. */
  async getRemitoCounts(): Promise<Record<string, number>> {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => {
        const counts: Record<string, number> = {};
        (req.result as StoredDocument[]).forEach((doc) => {
          if (doc.type === 'remito') {
            counts[doc.orderId] = (counts[doc.orderId] || 0) + 1;
          }
        });
        resolve(counts);
      };
      req.onerror = () => reject(req.error);
    });
  },
};
